import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn } from '@/components/base'

/**
 * How much room the message takes. The three cover what the app actually does
 * with an empty region, and they exist because the alternative — a bare `<p>`
 * per screen — produced eleven paddings and two greys for one sentence.
 */
const LAYOUTS = {
  /** A screen with nothing on it: the icon disc and room around it. */
  page: 'flex-col items-center justify-center gap-4 px-6 py-20 text-center',
  /** A list body, a dropdown, a section of a pane. */
  block: 'flex-col items-center justify-center gap-1.5 px-4 py-8 text-center',
  /** A line where a row would be — inside a section that has its own padding. */
  inline: 'flex-col gap-1',
} as const

/** How the mark is drawn, where the layout has room for one. `page` announces
 *  and gets the disc; `inline` is a sentence and gets nothing. */
const ICON = {
  page: { size: 32, wrapper: 'rounded-full bg-line-soft p-5' },
  block: { size: 20, wrapper: '' },
  inline: null,
} as const

const TEXT = {
  page: 'text-lg font-semibold text-foreground',
  block: 'text-sm font-medium text-foreground-2',
  inline: 'text-xs text-faint',
} as const

/** "There is nothing here" — the same shape wherever a list comes back empty. */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  layout = 'block',
  className,
}: {
  /** Drawn above the title. `inline` is a sentence and draws none. */
  icon?: LucideIcon
  title: string
  description?: string
  /** A way out of the empty state — usually the button that creates the first row. */
  action?: ReactNode
  layout?: keyof typeof LAYOUTS
  className?: string
}) {
  return (
    <div className={cn('flex', LAYOUTS[layout], className)}>
      {Icon && ICON[layout] ? (
        <div className={ICON[layout].wrapper}>
          <Icon size={ICON[layout].size} className="text-faint" aria-hidden />
        </div>
      ) : null}
      <div
        className={cn(
          'flex flex-col gap-2',
          layout !== 'inline' && 'items-center',
        )}
      >
        <p className={TEXT[layout]}>{title}</p>
        {description ? (
          <p
            className={cn(
              'text-muted-foreground',
              layout === 'page' ? 'max-w-md text-center text-sm' : 'text-xs',
            )}
          >
            {description}
          </p>
        ) : null}
        {action}
      </div>
    </div>
  )
}
